import { useState } from "react";
import { Formik } from "formik";
import { FormattedMessage, useIntl } from "react-intl";

import {
  isNotEmpty,
  isEmpty,
  Form,
  getLanguagesList,
  FilesUpload,
  TextInput,
  BadRequestError,
  useBrowser,
  useNotifier,
  useTranslateError,
  useApi,
} from "lcm-iot-commons";
import SubmitButton from "lcm-iot-commons/client/lib/components/Form/SubmitButton";
import CancelButton from "lcm-iot-commons/client/lib/components/Form/CancelButton";
import TypeaheadBox from "lcm-iot-commons/client/lib/components/Form/TypeaheadBox";
import { useParams } from "react-router-dom";
import AttachmentOverrideModal from "../Attachments/AttachmentOverrideModal";

type OwnProps = {
  addMode?: boolean;
  createMode?: boolean;
  initialValues?: {
    attachments?: $TSFixMe;
    name?: string;
    description?: string;
    category?: {
      name?: string;
    };
    validFrom?: string;
    validUntil?: string;
  };
  documentCategories?: {
    id?: number;
    name?: string;
    code?: string;
  }[];
};

const defaultProps: Partial<OwnProps> = {
  documentCategories: [],
  addMode: false,
  createMode: false,
  initialValues: {},
};

type Props = OwnProps & typeof defaultProps;

export default function DocumentForm({
  addMode,
  initialValues,
  documentCategories,
  createMode,
}: Props) {
  const [isDeclined, setIsDeclined] = useState(false);
  const [overrideKnownError, setOverrideKnownError] = useState<{
    fileUpload?: string;
  } | null>(null);

  const api = useApi();
  const browser = useBrowser();
  const intl = useIntl();
  const notifier = useNotifier();
  const translateError = useTranslateError();
  const { documentId, model, id } = useParams<{
    documentId: string;
    model: string;
    id: string;
  }>();

  const languages = getLanguagesList();

  const { mutateAsync: createDocument, data: createdDocument } =
    api.post.useMutation("/documents", {
      onError: () => {},
    });
  const { mutateAsync: addCategoryToDocument } = api.post.useMutation(
    "/documents/:id/categories",
    {
      onError: /* istanbul ignore next */ () => {},
    },
  );

  const { mutateAsync: addAttToDocument } = api.upload.useMutation(
    "/attachments",
    {
      onError: () => {},
    },
  );
  const { mutateAsync: addDocumentToProduct } = api.post.useMutation(
    `/products/${id}/documents`,
    {
      onError: /* istanbul ignore next */ () => {},
    },
  );
  const { mutateAsync: deleteDocument } = api.delete.useMutation(
    `/documents/${(createdDocument as $TSFixMe)?.id}`,
  );

  const DATE_REGEX = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

  const validateDateFields = (values: $TSFixMe) => {
    const errors: $TSFixMe = {};

    if (values.contentDate && !DATE_REGEX.test(values.contentDate)) {
      errors.contentDate = intl.formatMessage({
        id: "validation.valid_from.invalid",
      });
    }

    if (createMode) {
      if (values.validFrom && !DATE_REGEX.test(values.validFrom)) {
        errors.validFrom = intl.formatMessage({
          id: "validation.valid_from.invalid",
        });
      }

      if (values.validUntil && !DATE_REGEX.test(values.validUntil)) {
        errors.validUntil = intl.formatMessage({
          id: "validation.valid_until.invalid",
        });
      }

      if (
        values.validFrom &&
        values.validUntil &&
        values.validFrom > values.validUntil
      ) {
        errors.validUntil = intl.formatMessage({
          id: "validation.valid_until.must_be_older",
        });
      }
    }

    return errors;
  };

  const validateForm = (values: $TSFixMe) => {
    let errors = {};

    if (isEmpty(values.name) || isEmpty(values.name.trim())) {
      (errors as $TSFixMe).name = intl.formatMessage({
        id: "validation.name.mandatory",
      });
    } else if (values.name.length > 255) {
      (errors as $TSFixMe).name = intl.formatMessage(
        { id: "validation.name.too_long" },
        { characters: 255 },
      );
    }

    if (!values.language) {
      (errors as $TSFixMe).language = intl.formatMessage({
        id: "validation.language.mandatory",
      });
    }

    errors = { ...errors, ...validateDateFields(values) };

    if (!values.fileUpload || isEmpty(values.fileUpload.acceptedFiles)) {
      (errors as $TSFixMe).fileUpload = intl.formatMessage({
        id: "validation.file.mandatory",
      });
    }

    return errors;
  };

  const onOverrideDecline = () => {
    setIsDeclined(true);
  };

  const onCloseModal = (error: $TSFixMe) => {
    setIsDeclined(true);
    if (error instanceof BadRequestError) {
      // @ts-expect-error TS(2345) FIXME: Argument of type '"file"' is not assignable to par... Remove this comment to see the full error message
      if (error.contains("spoofed_media_type", "file")) {
        setOverrideKnownError({
          fileUpload: intl.formatMessage({
            id: "document_create.dropzone.rejection.illegal",
          }),
        });
      } else if (error.contains("invalid_input")) {
        setOverrideKnownError({
          fileUpload: intl.formatMessage({
            id: "document_create.dropzone.rejection.security",
          }),
        });
      } else {
        setOverrideKnownError({
          fileUpload: intl.formatMessage({ id: "unknown_error.details" }),
        });
      }
    }
  };

  const renderForm = (formProps: $TSFixMe) => {
    const { isSubmitting, status, errors } = formProps;

    const fileUploadErrors = overrideKnownError ?? errors;

    let overrideModal;
    if (status === "duplicated_attachment" && !isDeclined) {
      let values = { ...formProps.values };
      if (values.version === "-") {
        values = { ...values, version: undefined };
      }
      overrideModal = (
        <AttachmentOverrideModal
          // @ts-expect-error TS(2322) FIXME: Type '{ id: string; values: any; params: { documentId: any... Remove this comment to see the full error message
          id="attachment-override-modal"
          values={values}
          params={{ documentId, model, id }}
          onDecline={onOverrideDecline}
          onClose={onCloseModal}
        />
      );
    }

    return (
      <Form id="document-form" {...formProps}>
        <h3 style={{ marginTop: "10px" }}>
          <FormattedMessage id="document_details.header" />
        </h3>
        <TextInput
          {...formProps}
          id="document-name"
          name="name"
          label={intl.formatMessage({ id: "label.name" })}
          required={createMode}
          autoFocus={createMode}
          disabled={addMode}
          intl
        />
        <TypeaheadBox
          {...formProps}
          id="document-category"
          name="category"
          label={intl.formatMessage({ id: "label.category" })}
          options={documentCategories}
          disabled={addMode}
          intl
        />
        <TextInput
          {...formProps}
          id="document-version"
          name="version"
          label={intl.formatMessage({ id: "label.version" })}
          disabled={addMode}
          intl
        />
        <TextInput
          {...formProps}
          id="document-valid-from"
          name="validFrom"
          label={intl.formatMessage({ id: "label.valid_from" })}
          disabled={addMode}
          intl
          placeholder={intl.formatMessage({ id: "placeholder.date_format" })}
        />
        <TextInput
          {...formProps}
          id="document-valid-until"
          name="validUntil"
          label={intl.formatMessage({ id: "label.valid_until" })}
          disabled={addMode}
          intl
          placeholder={intl.formatMessage({ id: "placeholder.date_format" })}
        />
        <h3 style={{ marginTop: "30px" }}>
          <FormattedMessage id="attachment_details.header" />
        </h3>
        <TypeaheadBox
          {...formProps}
          id="document-attachment-language"
          name="language"
          label={intl.formatMessage({ id: "label.language" })}
          options={languages}
          intl
          autoFocus={addMode}
          required
        />
        <TextInput
          {...formProps}
          id="document-content-date"
          name="contentDate"
          label={intl.formatMessage({ id: "label.content_date" })}
          intl
          placeholder={intl.formatMessage({ id: "placeholder.date_format" })}
        />
        <FilesUpload
          {...formProps}
          multiple={false}
          id="attachment-upload"
          dropzoneInfoPrefix="add_attachment"
          name="fileUpload"
          errors={fileUploadErrors}
        />
        <div className="btn-group">
          <SubmitButton
            id="document-upload-submit"
            fetching={isSubmitting}
            disabled={!formProps.dirty}
          />
          <CancelButton
            id="document-upload-cancel"
            fetching={isSubmitting}
            // @ts-expect-error TS(2322) FIXME: Type '{ id: string; fetching: any; intl: IntlShape... Remove this comment to see the full error message
            intl={intl}
          />
        </div>
        {overrideModal}
      </Form>
    );
  };

  const onError = (error: $TSFixMe, actions: $TSFixMe) => {
    const formErrors = {};
    if (
      error instanceof BadRequestError &&
      // @ts-expect-error TS(2345) FIXME: Argument of type '"file"' is not assignable to par... Remove this comment to see the full error message
      error.contains("spoofed_media_type", "file")
    ) {
      (formErrors as $TSFixMe).fileUpload = intl.formatMessage({
        id: "document_create.dropzone.rejection.illegal",
      });
    } else if (
      error instanceof BadRequestError &&
      error.contains("invalid_input")
    ) {
      (formErrors as $TSFixMe).fileUpload = intl.formatMessage({
        id: "document_create.dropzone.rejection.security",
      });
    } else {
      notifier.showError(translateError(error));
    }
    actions.setErrors(formErrors);
  };

  const submitData = async (values: $TSFixMe, actions: $TSFixMe) => {
    let docId = documentId;
    const attachment = values.fileUpload.acceptedFiles[0];
    const { language, version, contentDate } = values;
    let date = {};
    let contentVersion = {};

    if (contentDate) {
      date = { content_date: contentDate };
    }
    if (version) {
      contentVersion = { content_version: version };
    }

    if (createMode) {
      const createDocumentPayload = {
        name: values.name,
        valid_from: isNotEmpty(values.validFrom) ? values.validFrom : null,
        valid_until: isNotEmpty(values.validUntil) ? values.validUntil : null,
        document_version: values.version,
      };

      await createDocument(createDocumentPayload, {
        onSuccess: (data: $TSFixMe) => {
          docId = data.id;

          Promise.all([
            addDocumentToProduct({ documents: [{ id: docId }] }),
            values.category?.id
              ? addCategoryToDocument({
                  mutationBody: { categories: [{ id: values.category.id }] },
                  mutationUrl: `/documents/${docId}/categories`,
                })
              : null,
            addAttToDocument({
              ...date,
              ...contentVersion,
              document_id: docId,
              languages: [language.code],
              file: attachment,
            }),
          ])
            .then(async () => {
              notifier.showSuccess(
                intl.formatMessage({
                  id: "document_create.success_notification",
                }),
              );
              browser.navigateTo(`/${model}/${id}`);
            })
            .catch(async (error: $TSFixMe) => {
              await deleteDocument();
              onError(error, actions);
            })
            .finally(() => {
              actions.setSubmitting(false);
            });
        },
      });
    } else {
      await addAttToDocument(
        {
          ...date,
          ...contentVersion,
          document_id: docId,
          languages: [language.code],
          file: attachment,
        },
        {
          onSuccess: () => {
            notifier.showSuccess(
              intl.formatMessage({
                id: "document_create.success_notification",
              }),
            );
            browser.navigateTo(`/${model}/${id}`);
          },
        },
      );
    }
  };

  const onSubmit = async (values: $TSFixMe, actions: $TSFixMe) => {
    try {
      const { language, version } = values;

      if (
        addMode &&
        initialValues?.attachments?.find(
          (item: $TSFixMe) =>
            item.languages?.includes(language.code) &&
            item.content_version === version,
        )
      ) {
        actions.setStatus("duplicated_attachment");
      } else {
        await submitData(values, actions);
      }
    } catch (error) {
      onError(error, actions);
    } finally {
      actions.setSubmitting(false);
    }
  };

  const handleOnSubmit = async (values: $TSFixMe, actions: $TSFixMe) => {
    setOverrideKnownError(null);
    setIsDeclined(false);
    if (values.version === "-") {
      onSubmit({ ...values, version: undefined }, actions);
    } else {
      onSubmit(values, actions);
    }
  };

  return (
    <Formik
      render={renderForm}
      validate={validateForm}
      onSubmit={handleOnSubmit}
      initialValues={initialValues}
      validateOnChange
      validateOnBlur={false}
    >
      {renderForm}
    </Formik>
  );
}

DocumentForm.defaultProps = defaultProps;
